import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import API from '../utils/api';
import Layout from '../components/Layout';
import StatusBadge from '../components/StatusBadge';
import { formatCurrency, formatDate } from '../utils/format';

function fmtFileSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024)       return bytes + ' B';
  if (bytes < 1024*1024)  return (bytes/1024).toFixed(1) + ' KB';
  return (bytes/1024/1024).toFixed(1) + ' MB';
}

function DocTypeLabel({ type }) {
  const MAP = {
    pre_inspection_report: { label: 'Condition Report', color: 'bg-blue-100 text-blue-700' },
    inspection_image:      { label: 'Inspection Photo', color: 'bg-blue-100 text-blue-700' },
    bond_receipt:          { label: 'Bond Receipt',     color: 'bg-amber-100 text-amber-700' },
    bond_lodgement:        { label: 'Bond Lodgement',   color: 'bg-amber-100 text-amber-700' },
    lease_agreement:       { label: 'Lease Agreement',  color: 'bg-purple-100 text-purple-700' },
    other:                 { label: 'Other',            color: 'bg-slate-100 text-slate-600' },
  };
  const m = MAP[type] || MAP.other;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${m.color}`}>{m.label}</span>;
}

function UploadZone({ sectionKey, acceptedTypes, sizeNote, onFile, uploading, uploadError }) {
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef(null);

  const handle = (file) => { if (file) onFile(file); };

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handle(e.dataTransfer.files[0]); }}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-brand-500 bg-blue-50' : 'border-slate-300 bg-slate-50 hover:border-brand-400 hover:bg-blue-50'
        }`}>
        <svg className="w-8 h-8 mx-auto mb-2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p className="text-sm font-medium text-slate-700">Drag files here or click to browse</p>
        <p className="text-xs text-slate-400 mt-1">{sizeNote}</p>
        <input ref={inputRef} type="file" accept={acceptedTypes} className="hidden"
          onChange={(e) => handle(e.target.files[0])} />
      </div>
      {uploading !== null && uploading !== undefined && (
        <div className="mt-2">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Uploading…</span><span>{uploading}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-1.5">
            <div className="bg-brand-600 h-1.5 rounded-full transition-all" style={{ width: `${uploading}%` }} />
          </div>
        </div>
      )}
      {uploadError && <p className="mt-2 text-xs text-red-600">{uploadError}</p>}
    </div>
  );
}
import { useAuth } from '../context/AuthContext';
import { generateVcatPdf } from '../utils/generateVcatPdf';
import { generateConsentPdf } from '../utils/generateConsentPdf';

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  PAID:       { label: 'Paid',    bg: 'bg-green-50',  text: 'text-green-700', dot: 'bg-green-500' },
  PARTIAL:    { label: 'Partial', bg: 'bg-amber-50',  text: 'text-amber-700', dot: 'bg-amber-400' },
  OVERDUE:    { label: 'Overdue', bg: '',             text: 'text-red-700',   dot: 'bg-red-400'   },
  NO_PAYMENT: { label: 'Pending', bg: 'bg-slate-50',  text: 'text-slate-500', dot: 'bg-slate-300' },
  AHEAD:      { label: 'Ahead',   bg: 'bg-blue-50',   text: 'text-blue-700',  dot: 'bg-blue-400'  },
};

const INSP_TYPES = [
  { value: 'routine',  label: 'Routine' },
  { value: 'entry',    label: 'Entry (move-in)' },
  { value: 'exit',     label: 'Exit (move-out)' },
  { value: 'drive_by', label: 'Drive-by' },
];

const PRIORITIES = [
  { value: 'low',    label: 'Low',    color: 'text-slate-500' },
  { value: 'medium', label: 'Medium', color: 'text-amber-600' },
  { value: 'high',   label: 'High',   color: 'text-orange-600' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-600' },
];

const MAINT_STATUSES = [
  { value: 'open',        label: 'Open',        bg: 'bg-red-50',    text: 'text-red-700'   },
  { value: 'in_progress', label: 'In progress', bg: 'bg-amber-50',  text: 'text-amber-700' },
  { value: 'resolved',    label: 'Resolved',    bg: 'bg-green-50',  text: 'text-green-700' },
];

const EXPENSE_CATS = [
  'Repairs & Maintenance', 'Council Rates', 'Insurance',
  'Land Tax', 'Water & Utilities', 'Advertising', 'Legal & Accounting',
  'Travel & Inspections', 'Other',
];

// ── Small reusable components ────────────────────────────────────────────────

function PeriodStatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.OVERDUE;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

function MaintStatusBadge({ status }) {
  const s = MAINT_STATUSES.find(x => x.value === status) || MAINT_STATUSES[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function PriorityDot({ priority }) {
  const p = PRIORITIES.find(x => x.value === priority) || PRIORITIES[1];
  return <span className={`text-xs font-medium ${p.color}`}>{p.label}</span>;
}

// ── Skeleton loader ──────────────────────────────────────────────────────────

function SkeletonLoader() {
  return (
    <Layout>
      <div className="h-5 w-36 bg-slate-200 rounded mb-5 animate-pulse" />
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-5 animate-pulse">
        <div className="h-6 bg-slate-200 rounded w-2/3 mb-2" />
        <div className="h-4 bg-slate-100 rounded w-1/3 mb-1" />
        <div className="h-3 bg-slate-100 rounded w-1/2" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[1,2,3,4].map(i => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 animate-pulse">
            <div className="h-3 bg-slate-200 rounded w-16 mb-3" />
            <div className="h-7 bg-slate-200 rounded w-24 mb-2" />
            <div className="h-3 bg-slate-100 rounded w-20" />
          </div>
        ))}
      </div>
    </Layout>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function LedgerPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const todayISO = new Date().toLocaleDateString('sv-SE');

  // Core ledger data
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Payment form
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [payForm, setPayForm]   = useState({ amount: '', receivedDate: todayISO, notes: '' });
  const [payError, setPayError] = useState('');
  const [payLoading, setPayLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  // Tabs
  const [activeTab, setActiveTab]     = useState('schedule');
  const [tabLoaded, setTabLoaded]     = useState(new Set(['schedule']));
  const [expandedPeriod, setExpandedPeriod] = useState(null);

  // Inline edit (payments)
  const [editingPayment, setEditingPayment] = useState(null);
  const [editError, setEditError]           = useState('');
  const [editLoading, setEditLoading]       = useState(false);

  // Phone edit
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue]     = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError]     = useState('');

  // Lease management
  const [showLeaseModal, setShowLeaseModal]   = useState(false);
  const [leaseForm, setLeaseForm]             = useState({ leaseType: 'fixed', leaseEndDate: '' });
  const [leaseLoading, setLeaseLoading]       = useState(false);

  // End tenancy
  const [showEndModal, setShowEndModal]       = useState(false);
  const [endLoading, setEndLoading]           = useState(false);

  // ── Inspections ──
  const [inspections, setInspections]   = useState([]);
  const [inspLoading, setInspLoading]   = useState(false);
  const [showAddInsp, setShowAddInsp]   = useState(false);
  const [inspForm, setInspForm]         = useState({ inspectionDate: todayISO, type: 'routine', notes: '' });
  const [editingInsp, setEditingInsp]   = useState(null);

  // ── Maintenance ──
  const [maintenance, setMaintenance]   = useState([]);
  const [maintLoading, setMaintLoading] = useState(false);
  const [showAddMaint, setShowAddMaint] = useState(false);
  const [maintForm, setMaintForm]       = useState({ title: '', description: '', priority: 'medium' });
  const [editingMaint, setEditingMaint] = useState(null);

  // ── Expenses ──
  const [expenses, setExpenses]         = useState([]);
  const [expLoading, setExpLoading]     = useState(false);
  const [showAddExp, setShowAddExp]     = useState(false);
  const [expForm, setExpForm]           = useState({ expenseDate: todayISO, category: 'Repairs & Maintenance', description: '', amount: '' });
  const [editingExp, setEditingExp]     = useState(null);

  // ── Resend invite ──
  const [resendLoading, setResendLoading] = useState(false);

  // ── Notices ──
  const [notices, setNotices]               = useState([]);
  const [noticesLoading, setNoticesLoading] = useState(false);
  const [showAddNotice, setShowAddNotice]   = useState(false);
  const [noticeForm, setNoticeForm]         = useState({ noticeType: 'notice_to_vacate', issuedDate: todayISO, deliveryMethod: 'email', vacateDate: '', notes: '' });
  const [editingNotice, setEditingNotice]   = useState(null);

  // ── VCAT report ──
  const [reports, setReports]               = useState([]);
  const [showVcatModal, setShowVcatModal]   = useState(false);
  const [vcatPreparedBy, setVcatPreparedBy] = useState('');
  const [vcatLoading, setVcatLoading]       = useState(false);
  const [showVcatSidebar, setShowVcatSidebar] = useState(true);

  // ── Electronic consent ──
  const [consentStatus, setConsentStatus]                   = useState(null);
  const [consentStatusLoading, setConsentStatusLoading]     = useState(false);
  const [showManualConsentModal, setShowManualConsentModal] = useState(false);
  const [manualConsentForm, setManualConsentForm]           = useState({ consentDate: '', consentHow: 'Included in signed lease agreement', notes: '' });
  const [manualConsentLoading, setManualConsentLoading]     = useState(false);
  const [manualConsentConfirmed, setManualConsentConfirmed] = useState(false);

  // ── Documents tab ──
  const [documents, setDocuments]             = useState([]);
  const [docsLoading, setDocsLoading]         = useState(false);
  const [docUploading, setDocUploading]       = useState({});
  const [docUploadError, setDocUploadError]   = useState({});
  const [lightboxDoc, setLightboxDoc]         = useState(null);

  // ── Import Ledger modal ──
  const [showImportModal, setShowImportModal]   = useState(false);
  const [importStep, setImportStep]             = useState(1);
  const [importFile, setImportFile]             = useState(null);
  const [importLoading, setImportLoading]       = useState(false);
  const [importError, setImportError]           = useState('');
  const [importExtraction, setImportExtraction] = useState(null);
  const [importLease, setImportLease]           = useState(null);
  const [importRows, setImportRows]             = useState([]);
  const [importResult, setImportResult]         = useState(null);

  const handleResendInvite = async () => {
    setResendLoading(true);
    try {
      await API.post(`/properties/${id}/resend-invite`);
      showToast('Invite email sent', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send invite', 'error');
    } finally {
      setResendLoading(false);
    }
  };

  const handleEndTenancy = async () => {
    setEndLoading(true);
    try {
      await API.post(`/properties/${id}/end-tenancy`);
      showToast('Tenancy ended', 'success');
      setShowEndModal(false);
      // Redirect back to dashboard — no active tenancy to show
      setTimeout(() => window.location.href = '/dashboard', 1200);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to end tenancy', 'error');
      setEndLoading(false);
    }
  };

  // ── ESC closes modal ──
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setLightboxDoc(null); setShowImportModal(false); setShowPaymentForm(false); setShowLeaseModal(false); setShowEndModal(false); setShowVcatModal(false); setShowAddNotice(false); setEditingNotice(null); setShowManualConsentModal(false); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ── Initial load ──
  useEffect(() => { loadLedger(); }, [id]); // eslint-disable-line

  // ── Lazy load tab data ──
  useEffect(() => {
    if (!data) return;
    if (activeTab === 'inspections' && !tabLoaded.has('inspections')) loadInspections();
    if (activeTab === 'maintenance' && !tabLoaded.has('maintenance')) loadMaintenance();
    if (activeTab === 'expenses'    && !tabLoaded.has('expenses'))    loadExpenses();
    if (activeTab === 'notices'     && !tabLoaded.has('notices'))     loadNotices();
    if (activeTab === 'tenant'      && !tabLoaded.has('tenant'))      loadConsentStatus();
    if (activeTab === 'documents'   && !tabLoaded.has('documents'))   loadDocuments();
  }, [activeTab, data]); // eslint-disable-line

  // ── Loaders ──
  const loadLedger = async () => {
    try {
      const { data: res } = await API.get(`/properties/${id}/ledger`);
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  const loadInspections = async () => {
    setInspLoading(true);
    try {
      const { data: rows } = await API.get(`/properties/${id}/inspections`);
      setInspections(rows);
      setTabLoaded(prev => new Set(prev).add('inspections'));
    } catch {} finally { setInspLoading(false); }
  };

  const loadMaintenance = async () => {
    setMaintLoading(true);
    try {
      const { data: rows } = await API.get(`/properties/${id}/maintenance`);
      setMaintenance(rows);
      setTabLoaded(prev => new Set(prev).add('maintenance'));
    } catch {} finally { setMaintLoading(false); }
  };

  const loadExpenses = async () => {
    setExpLoading(true);
    try {
      const { data: rows } = await API.get(`/properties/${id}/expenses`);
      setExpenses(rows);
      setTabLoaded(prev => new Set(prev).add('expenses'));
    } catch {} finally { setExpLoading(false); }
  };

  const loadNotices = async () => {
    setNoticesLoading(true);
    try {
      const { data: rows } = await API.get(`/properties/${id}/notices`);
      setNotices(rows);
      setTabLoaded(prev => new Set(prev).add('notices'));
    } catch {} finally { setNoticesLoading(false); }
  };

  const loadReports = async () => {
    try {
      const { data: rows } = await API.get(`/properties/${id}/reports`);
      setReports(rows);
    } catch {}
  };

  const loadConsentStatus = async () => {
    setConsentStatusLoading(true);
    try {
      const { data: cs } = await API.get(`/properties/${id}/consent-status`);
      setConsentStatus(cs);
      setTabLoaded(prev => new Set(prev).add('tenant'));
    } catch {} finally { setConsentStatusLoading(false); }
  };

  const handleManualConsent = async (e) => {
    e.preventDefault();
    setManualConsentLoading(true);
    try {
      await API.post(`/properties/${id}/manual-consent`, manualConsentForm);
      const { data: cs } = await API.get(`/properties/${id}/consent-status`);
      setConsentStatus(cs);
      setShowManualConsentModal(false);
      setManualConsentConfirmed(false);
      setManualConsentForm({ consentDate: '', consentHow: 'Included in signed lease agreement', notes: '' });
      showToast('Consent recorded ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to record consent', 'error'); }
    finally { setManualConsentLoading(false); }
  };

  // ── Toast ──
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  // ── Payment handlers ──
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setPayError('');
    if (payForm.receivedDate > todayISO) { setPayError('Payment date cannot be in the future'); return; }
    if (parseFloat(payForm.amount) <= 0) { setPayError('Amount must be greater than zero'); return; }
    setPayLoading(true);
    try {
      const amount = parseFloat(payForm.amount);
      await API.post(`/properties/${id}/payments`, { amount, receivedDate: payForm.receivedDate, notes: payForm.notes || undefined });
      setShowPaymentForm(false);
      setPayForm({ amount: '', receivedDate: todayISO, notes: '' });
      const { data: res } = await API.get(`/properties/${id}/ledger`);
      setData(res);
      showToast(`Payment of ${formatCurrency(amount)} recorded ✓`);
    } catch (err) { setPayError(err.response?.data?.error || 'Failed to record payment'); }
    finally { setPayLoading(false); }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Remove this payment?')) return;
    try {
      const { data: res } = await API.delete(`/properties/${id}/payments/${paymentId}`);
      setData(prev => ({ ...prev, ledger: res.ledger, periods: res.periods }));
      showToast('Payment removed', 'info');
    } catch {}
  };

  const startEditPayment = (payment) => {
    setEditingPayment({ id: payment.id, amount: payment.amount.toFixed(2), receivedDate: payment.received_date, notes: payment.notes || '' });
    setEditError('');
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditError('');
    setEditLoading(true);
    try {
      const { data: res } = await API.patch(`/properties/${id}/payments/${editingPayment.id}`,
        { amount: parseFloat(editingPayment.amount), receivedDate: editingPayment.receivedDate, notes: editingPayment.notes || '' });
      setData(prev => ({ ...prev, ledger: res.ledger, periods: res.periods }));
      setEditingPayment(null);
      showToast('Payment updated ✓');
    } catch (err) { setEditError(err.response?.data?.error || 'Failed to save'); }
    finally { setEditLoading(false); }
  };

  // ── Phone handler ──
  const handleSavePhone = async (e) => {
    e.preventDefault();
    setPhoneError('');
    setPhoneLoading(true);
    try {
      await API.patch(`/properties/${id}/phone`, { phone: phoneValue });
      setData(prev => ({ ...prev, tenancy: { ...prev.tenancy, tenant_phone: phoneValue || null } }));
      setEditingPhone(false);
      showToast('Phone number saved ✓');
    } catch (err) { setPhoneError(err.response?.data?.error || 'Failed to save phone'); }
    finally { setPhoneLoading(false); }
  };

  // ── Lease handler ──
  const handleSaveLease = async (e) => {
    e.preventDefault();
    setLeaseLoading(true);
    try {
      await API.patch(`/properties/${id}/lease`, {
        leaseType: leaseForm.leaseType,
        leaseEndDate: leaseForm.leaseEndDate || null,
      });
      setData(prev => ({ ...prev, tenancy: {
        ...prev.tenancy,
        lease_type: leaseForm.leaseType,
        lease_end_date: leaseForm.leaseEndDate || null,
      }}));
      setShowLeaseModal(false);
      showToast('Lease updated ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to update lease', 'error'); }
    finally { setLeaseLoading(false); }
  };

  // ── Inspection handlers ──
  const handleAddInsp = async (e) => {
    e.preventDefault();
    try {
      const { data: row } = await API.post(`/properties/${id}/inspections`, inspForm);
      setInspections(prev => [row, ...prev]);
      setShowAddInsp(false);
      setInspForm({ inspectionDate: todayISO, type: 'routine', notes: '' });
      showToast('Inspection logged ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to add inspection', 'error'); }
  };

  const handleSaveInsp = async (e) => {
    e.preventDefault();
    try {
      const { data: row } = await API.patch(`/properties/${id}/inspections/${editingInsp.id}`, editingInsp);
      setInspections(prev => prev.map(x => x.id === row.id ? row : x));
      setEditingInsp(null);
      showToast('Inspection updated ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to save', 'error'); }
  };

  const handleDeleteInsp = async (inspId) => {
    if (!window.confirm('Delete this inspection?')) return;
    await API.delete(`/properties/${id}/inspections/${inspId}`);
    setInspections(prev => prev.filter(x => x.id !== inspId));
    showToast('Inspection deleted', 'info');
  };

  // ── Maintenance handlers ──
  const handleAddMaint = async (e) => {
    e.preventDefault();
    try {
      const { data: row } = await API.post(`/properties/${id}/maintenance`, maintForm);
      setMaintenance(prev => [row, ...prev]);
      setShowAddMaint(false);
      setMaintForm({ title: '', description: '', priority: 'medium' });
      showToast('Request added ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to add', 'error'); }
  };

  const handleSaveMaint = async (e) => {
    e.preventDefault();
    try {
      const { data: row } = await API.patch(`/properties/${id}/maintenance/${editingMaint.id}`, editingMaint);
      setMaintenance(prev => prev.map(x => x.id === row.id ? row : x));
      setEditingMaint(null);
      showToast('Request updated ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to save', 'error'); }
  };

  const handleDeleteMaint = async (reqId) => {
    if (!window.confirm('Delete this request?')) return;
    await API.delete(`/properties/${id}/maintenance/${reqId}`);
    setMaintenance(prev => prev.filter(x => x.id !== reqId));
    showToast('Request deleted', 'info');
  };

  // ── Expense handlers ──
  const handleAddExp = async (e) => {
    e.preventDefault();
    try {
      const { data: row } = await API.post(`/properties/${id}/expenses`, expForm);
      setExpenses(prev => [row, ...prev]);
      setShowAddExp(false);
      setExpForm({ expenseDate: todayISO, category: 'Repairs & Maintenance', description: '', amount: '' });
      showToast('Expense recorded ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to add expense', 'error'); }
  };

  const handleSaveExp = async (e) => {
    e.preventDefault();
    try {
      const { data: row } = await API.patch(`/properties/${id}/expenses/${editingExp.id}`, editingExp);
      setExpenses(prev => prev.map(x => x.id === row.id ? row : x));
      setEditingExp(null);
      showToast('Expense updated ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to save', 'error'); }
  };

  const handleDeleteExp = async (expId) => {
    if (!window.confirm('Delete this expense?')) return;
    await API.delete(`/properties/${id}/expenses/${expId}`);
    setExpenses(prev => prev.filter(x => x.id !== expId));
    showToast('Expense deleted', 'info');
  };

  // ── Notice handlers ──
  const handleAddNotice = async (e) => {
    e.preventDefault();
    try {
      const { data: row } = await API.post(`/properties/${id}/notices`, {
        noticeType:     noticeForm.noticeType,
        issuedDate:     noticeForm.issuedDate,
        deliveryMethod: noticeForm.deliveryMethod,
        vacateDate:     noticeForm.vacateDate || null,
        notes:          noticeForm.notes || null,
      });
      setNotices(prev => [row, ...prev]);
      setShowAddNotice(false);
      setNoticeForm({ noticeType: 'notice_to_vacate', issuedDate: todayISO, deliveryMethod: 'email', vacateDate: '', notes: '' });
      showToast('Notice logged ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to add notice', 'error'); }
  };

  const handleSaveNotice = async (e) => {
    e.preventDefault();
    try {
      const { data: row } = await API.patch(`/properties/${id}/notices/${editingNotice.id}`, {
        noticeType:     editingNotice.notice_type,
        issuedDate:     editingNotice.issued_date,
        deliveryMethod: editingNotice.delivery_method,
        vacateDate:     editingNotice.vacate_date || null,
        notes:          editingNotice.notes || null,
      });
      setNotices(prev => prev.map(x => x.id === row.id ? row : x));
      setEditingNotice(null);
      showToast('Notice updated ✓');
    } catch (err) { showToast(err.response?.data?.error || 'Failed to save', 'error'); }
  };

  const handleDeleteNotice = async (noticeId) => {
    if (!window.confirm('Delete this notice record?')) return;
    await API.delete(`/properties/${id}/notices/${noticeId}`);
    setNotices(prev => prev.filter(x => x.id !== noticeId));
    showToast('Notice deleted', 'info');
  };

  // ── VCAT report handler ──
  const handleGenerateVcat = async () => {
    setVcatLoading(true);
    try {
      await API.post(`/properties/${id}/reports`, {
        reportType:              'vcat',
        preparedBy:              vcatPreparedBy || user?.name || '',
        periodsCovered:          periods.length,
        amountOutstandingCents:  Math.round((ledger.arrearsAmount || 0) * 100),
      });
      // Fetch latest documents for VCAT PDF supporting documents section
      let vcatDocs = documents;
      if (vcatDocs.length === 0) {
        try {
          const { data: docRes } = await API.get(`/properties/${id}/documents`);
          vcatDocs = docRes.documents || [];
        } catch {}
      }
      generateVcatPdf({
        property, tenancy, ledger, periods, notices, reports,
        landlordName: user?.name || '',
        preparedBy:   vcatPreparedBy || user?.name || '',
        consentStatus,
        documents: vcatDocs,
      });
      setShowVcatModal(false);
      showToast('VCAT report downloaded ✓');
      loadReports();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to generate report', 'error');
    } finally { setVcatLoading(false); }
  };

  const loadDocuments = async () => {
    setDocsLoading(true);
    try {
      const { data: res } = await API.get(`/properties/${id}/documents`);
      setDocuments(res.documents || []);
      setTabLoaded(prev => new Set(prev).add('documents'));
    } catch {} finally { setDocsLoading(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      await API.delete(`/properties/${id}/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      showToast('Document deleted');
    } catch { showToast('Failed to delete document', 'error'); }
  };

  const handleDocUpload = async (file, documentType, sectionKey) => {
    setDocUploadError(prev => ({ ...prev, [sectionKey]: '' }));
    setDocUploading(prev => ({ ...prev, [sectionKey]: 0 }));
    const form = new FormData();
    form.append('file', file);
    form.append('document_type', documentType);
    try {
      await API.post(`/properties/${id}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setDocUploading(prev => ({ ...prev, [sectionKey]: Math.round((e.loaded * 100) / e.total) }));
        },
      });
      await loadDocuments();
      showToast('File uploaded ✓');
    } catch (err) {
      setDocUploadError(prev => ({ ...prev, [sectionKey]: err.response?.data?.error || 'Upload failed. Check file size and type.' }));
    } finally {
      setDocUploading(prev => ({ ...prev, [sectionKey]: null }));
    }
  };

  const getDocFileUrl = (docId) => {
    const base = API.defaults.baseURL || '';
    return `${base}/properties/${id}/documents/${docId}/file`;
  };

  const handleImportAnalyse = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportError('');
    const form = new FormData();
    form.append('ledger', importFile);
    try {
      const { data } = await API.post(`/properties/${id}/import-ledger`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportExtraction(data.extraction);
      setImportLease(data.lease);
      const rows = (data.extraction.payments || []).map((p, i) => ({
        _key:        i,
        selected:    p.confidence !== 'low' && !p.isDuplicate,
        payment_date: p.payment_date || '',
        amount:      String(p.amount || ''),
        description: p.description || '',
        confidence:  p.confidence,
        isDuplicate: !!p.isDuplicate,
      }));
      setImportRows(rows);
      setImportStep(2);
    } catch (err) {
      setImportError(err.response?.data?.error || 'Could not read this document. Try a clearer image or a different file format.');
    } finally { setImportLoading(false); }
  };

  const handleImportConfirm = async () => {
    const selected = importRows.filter(r => r.selected && !r.isDuplicate);
    if (selected.length === 0) return;
    setImportLoading(true);
    setImportError('');
    try {
      const { data } = await API.post(`/properties/${id}/import-ledger/confirm`, {
        payments: selected.map(r => ({
          payment_date: r.payment_date,
          amount:       parseFloat(r.amount),
          description:  r.description,
        })),
      });
      setImportResult(data);
      setImportStep(3);
      const { data: fresh } = await API.get(`/properties/${id}/ledger`);
      setData(fresh);
    } catch (err) {
      setImportError(err.response?.data?.error || 'Import failed. Please try again.');
    } finally { setImportLoading(false); }
  };

  const resetImportModal = () => {
    setShowImportModal(false);
    setImportStep(1);
    setImportFile(null);
    setImportLoading(false);
    setImportError('');
    setImportExtraction(null);
    setImportLease(null);
    setImportRows([]);
    setImportResult(null);
  };

  const handleGenerateConsentPdf = () => {
    generateConsentPdf({
      property,
      tenancy,
      consentStatus,
      landlordName: user?.name || '',
    });
  };

  // ── Render guards ──
  if (loading) return <SkeletonLoader />;
  if (error) return (
    <Layout>
      <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl text-sm">{error}</div>
    </Layout>
  );

  const { property, tenancy, ledger, periods = [] } = data;
  const inviteLink = `${window.location.origin}/tenant/invite/${tenancy.invite_token}`;

  // Period counts
  const paidCount    = periods.filter(p => p.status === 'PAID').length;
  const overdueCount = periods.filter(p => p.status === 'OVERDUE').length;
  const partialCount = periods.filter(p => p.status === 'PARTIAL').length;
  const aheadCount   = periods.filter(p => p.status === 'AHEAD').length;
  const statParts = [
    paidCount    > 0 && { label: `${paidCount} paid`,    color: '#15803D' },
    aheadCount   > 0 && { label: `${aheadCount} ahead`,  color: '#1D4ED8' },
    partialCount > 0 && { label: `${partialCount} partial`, color: '#B45309' },
    overdueCount > 0 && { label: `${overdueCount} overdue`, color: '#DC2626' },
  ].filter(Boolean);

  // Card 1 colours
  const card1 = !ledger.inArrears
    ? { bg: '#DCFCE7', border: '#16A34A', val: '#15803D', sub: '#16A34A' }
    : ledger.arrearsDays <= 7
      ? { bg: '#FEF9C3', border: '#CA8A04', val: '#92400E', sub: '#CA8A04' }
      : { bg: '#FEE2E2', border: '#DC2626', val: '#991B1B', sub: '#DC2626' };
  const card1Sub = !ledger.inArrears
    ? (ledger.daysAhead > 0 ? `${ledger.daysAhead} day${ledger.daysAhead !== 1 ? 's' : ''} ahead` : 'Up to date')
    : `${ledger.arrearsDays} day${ledger.arrearsDays !== 1 ? 's' : ''} overdue`;
  const card4 = !ledger.inArrears
    ? { color: '#15803D', label: 'All clear ✓' }
    : ledger.arrearsDays <= 7
      ? { color: '#92400E', label: `${formatCurrency(ledger.arrearsAmount)} owing` }
      : { color: '#991B1B', label: `${formatCurrency(ledger.arrearsAmount)} owing · ${ledger.arrearsDays} days` };
  const monthLabel = new Date().toLocaleString('en-AU', { month: 'long', year: 'numeric' });

  // Lease status helpers
  const leaseType    = tenancy.lease_type || 'fixed';
  const leaseEndDate = tenancy.lease_end_date;
  const isExpired    = leaseEndDate && leaseEndDate < todayISO;
  const leaseBadge = leaseType === 'periodic'
    ? { label: 'Month to month', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' }
    : leaseEndDate
      ? isExpired
        ? { label: `Fixed term expired ${formatDate(leaseEndDate)}`, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' }
        : { label: `Fixed term · until ${formatDate(leaseEndDate)}`, bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' }
      : { label: 'Fixed term · no end date set', bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200' };

  // Expenses: current Australian FY (Jul–Jun)
  const now = new Date();
  const fyStart = now.getMonth() >= 6 ? `${now.getFullYear()}-07-01` : `${now.getFullYear()-1}-07-01`;
  const fyEnd   = now.getMonth() >= 6 ? `${now.getFullYear()+1}-06-30` : `${now.getFullYear()}-06-30`;
  const fyLabel = now.getMonth() >= 6 ? `FY${now.getFullYear()}-${String(now.getFullYear()+1).slice(-2)}` : `FY${now.getFullYear()-1}-${String(now.getFullYear()).slice(-2)}`;
  const fyTotal = expenses.filter(e => e.expense_date >= fyStart && e.expense_date <= fyEnd).reduce((s, e) => s + e.amount, 0);

  const TABS = [
    { key: 'schedule',    label: 'Rent schedule' },
    { key: 'payments',    label: 'Payments' },
    { key: 'inspections', label: 'Inspections' },
    { key: 'maintenance', label: 'Maintenance' },
    { key: 'expenses',    label: 'Expenses' },
    { key: 'notices',     label: notices.length > 0 ? `Notices (${notices.length})` : 'Notices' },
    { key: 'tenant',      label: 'Tenant' },
    { key: 'documents',   label: 'Documents' },
  ];

  // VCAT helpers
  const twelveMonthsAgo = new Date(); twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
  const strikeCount = notices.filter(n => new Date(n.issued_date) >= twelveMonthsAgo).length;

  return (
    <Layout>
      {/* ── Toast ── */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === 'error' ? 'bg-red-600 text-white' :
          toast.type === 'info'  ? 'bg-slate-700 text-white' : 'bg-green-700 text-white'
        }`}>
          {toast.type === 'error'
            ? <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
            : toast.type === 'info'
              ? <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              : <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
          }
          {toast.msg}
        </div>
      )}

      {/* ── Back ── */}
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-5 transition-colors">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to properties
      </Link>

      {/* ── Header card ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-xl font-bold text-slate-800">{property.address}</h1>
              <StatusBadge inArrears={ledger.inArrears} arrearsDays={ledger.arrearsDays} arrearsAmount={ledger.arrearsAmount} />
            </div>
            <p className="text-sm text-slate-500">
              {tenancy.tenant_name}
              {tenancy.tenant_email && <span className="text-slate-400"> · {tenancy.tenant_email}</span>}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              Lease started {formatDate(tenancy.lease_start_date)} · {formatCurrency(tenancy.fortnightly_rent)} per fortnight
            </p>
            {/* Lease status badge */}
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ${leaseBadge.bg} ${leaseBadge.text} ${leaseBadge.border}`}>
                {leaseBadge.label}
              </span>
              <button
                onClick={() => {
                  setLeaseForm({ leaseType, leaseEndDate: leaseEndDate || '' });
                  setShowLeaseModal(true);
                }}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2">
                Update
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2 self-start">
            <button
              onClick={() => { setShowImportModal(true); setImportStep(1); }}
              title="Import payment history from a PDF or image ledger using AI"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 min-h-[36px]">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Import ledger
            </button>
            <button
              onClick={() => { setPayForm({ amount: '', receivedDate: todayISO, notes: '' }); setPayError(''); setShowPaymentForm(true); }}
              className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 min-h-[36px]">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Record payment
            </button>
            <button
              onClick={() => setShowEndModal(true)}
              className="border border-red-200 text-red-600 hover:bg-red-50 px-4 py-2 rounded-lg text-xs font-medium transition-colors min-h-[32px]">
              End tenancy
            </button>
            {ledger.inArrears && ledger.arrearsDays >= 14 && (
              <button
                onClick={() => { setVcatPreparedBy(user?.name || ''); loadReports(); setShowVcatModal(true); }}
                className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-xs font-semibold transition-colors min-h-[32px] flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                VCAT Report
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── 4 Metric cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="rounded-2xl border p-4" style={{ backgroundColor: card1.bg, borderColor: card1.border }}>
          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: card1.sub }}>Paid to date</p>
          <p className="font-bold leading-tight" style={{ color: card1.val, fontSize: '26px' }}>{formatDate(ledger.paidToDate)}</p>
          <p className="text-xs mt-1 font-medium" style={{ color: card1.sub }}>{card1Sub}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Next due</p>
          <p className="text-lg font-bold text-slate-800 leading-tight">{formatDate(ledger.upcomingDueDate)}</p>
          <p className="text-xs text-slate-400 mt-1">{formatCurrency(ledger.nextDueAmount)} due</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">This month</p>
          <p className="text-lg font-bold text-slate-800 leading-tight font-mono">{formatCurrency(ledger.monthReceived ?? 0)}</p>
          <p className="text-xs text-slate-400 mt-1">received in {monthLabel}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Balance</p>
          <p className="text-base font-bold leading-snug" style={{ color: card4.color }}>{card4.label}</p>
        </div>
      </div>

      {/* ── Payment modal ── */}
      {showPaymentForm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowPaymentForm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">Record a payment</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {ledger.balanceOwing > 0
                      ? <>{formatCurrency(ledger.balanceOwing)} outstanding · {formatCurrency(tenancy.fortnightly_rent)} per fortnight</>
                      : <>Up to date · {formatCurrency(tenancy.fortnightly_rent)} per fortnight</>}
                  </p>
                </div>
                <button onClick={() => setShowPaymentForm(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5">
                <div className="mb-4">
                  <p className="text-xs font-medium text-slate-500 mb-2">Quick fill</p>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setPayForm(f => ({ ...f, amount: tenancy.fortnightly_rent.toFixed(2) }))}
                      className="text-xs px-3 py-1.5 rounded-lg border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 font-medium min-h-[32px]">
                      Full rent {formatCurrency(tenancy.fortnightly_rent)}
                    </button>
                    {ledger.balanceOwing > 0 && (
                      <button type="button" onClick={() => setPayForm(f => ({ ...f, amount: ledger.balanceOwing.toFixed(2) }))}
                        className="text-xs px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium min-h-[32px]">
                        Clear arrears {formatCurrency(ledger.balanceOwing)}
                      </button>
                    )}
                  </div>
                </div>
                {payError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{payError}</div>}
                <form onSubmit={handleRecordPayment} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Amount (AUD)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                        <input type="number" step="0.01" min="0.01" required value={payForm.amount}
                          onChange={e => setPayForm({ ...payForm, amount: e.target.value })}
                          className="w-full pl-8 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Date received</label>
                      <input type="date" value={payForm.receivedDate} max={todayISO}
                        onChange={e => setPayForm({ ...payForm, receivedDate: e.target.value })} required
                        className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Notes (optional)</label>
                    <input value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })}
                      placeholder="e.g. Bank transfer"
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  </div>
                  <div className="flex gap-3 pt-1">
                    <button type="button" onClick={() => setShowPaymentForm(false)}
                      className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]">Cancel</button>
                    <button type="submit" disabled={payLoading}
                      className="flex-1 px-4 py-2.5 bg-green-700 hover:bg-green-800 disabled:opacity-60 text-white rounded-lg text-sm font-semibold min-h-[44px]">
                      {payLoading ? 'Saving...' : 'Save payment'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Lease modal ── */}
      {showLeaseModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowLeaseModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm pointer-events-auto">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-800">Update lease status</h2>
                <button onClick={() => setShowLeaseModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <form onSubmit={handleSaveLease} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Lease type</label>
                  <div className="flex gap-2">
                    {[{v:'fixed',l:'Fixed term'},{v:'periodic',l:'Month to month'}].map(opt => (
                      <button key={opt.v} type="button"
                        onClick={() => setLeaseForm(f => ({ ...f, leaseType: opt.v }))}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${
                          leaseForm.leaseType === opt.v
                            ? 'bg-slate-800 text-white border-slate-800'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                        }`}>{opt.l}</button>
                    ))}
                  </div>
                </div>
                {leaseForm.leaseType === 'fixed' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {leaseEndDate ? 'Lease end date' : 'Set lease end date (optional)'}
                    </label>
                    <input type="date" value={leaseForm.leaseEndDate}
                      onChange={e => setLeaseForm(f => ({ ...f, leaseEndDate: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  </div>
                )}
                {leaseForm.leaseType === 'periodic' && (
                  <p className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                    Under Victorian tenancy law, a fixed-term lease automatically becomes periodic (month to month) after the end date unless either party gives notice.
                  </p>
                )}
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowLeaseModal(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]">Cancel</button>
                  <button type="submit" disabled={leaseLoading}
                    className="flex-1 px-4 py-2.5 bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white rounded-lg text-sm font-semibold min-h-[44px]">
                    {leaseLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* ── End Tenancy Modal ── */}
      {showEndModal && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowEndModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-800">End this tenancy?</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{tenancy.tenant_name} · {property.address}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 mb-2">This will mark the tenancy as ended. You can add a new tenant to this property afterwards.</p>
              {ledger.inArrears && (
                <div className="mb-4 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                  <strong>Note:</strong> This tenant is currently {ledger.arrearsDays} day{ledger.arrearsDays !== 1 ? 's' : ''} in arrears with {formatCurrency(ledger.arrearsAmount)} outstanding.
                </div>
              )}
              <div className="flex gap-3 mt-4">
                <button onClick={() => setShowEndModal(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]">
                  Cancel
                </button>
                <button onClick={handleEndTenancy} disabled={endLoading}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-lg text-sm font-semibold min-h-[44px]">
                  {endLoading ? 'Ending…' : 'End tenancy'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── VCAT Report Modal ── */}
      {showVcatModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowVcatModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-slate-800">Generate VCAT Evidence Report</h2>
                    <p className="text-xs text-slate-500 mt-0.5">PDF download · {periods.length} periods · {formatCurrency(ledger.arrearsAmount || 0)} outstanding</p>
                  </div>
                </div>
                <button onClick={() => setShowVcatModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-violet-50 border border-violet-100 rounded-lg p-3 text-xs text-violet-800">
                  This report includes a payment history table, arrears summary, and any notices logged in the last 12 months. It is suitable for filing with VCAT.
                </div>
                {strikeCount > 0 && (
                  <div className={`rounded-lg px-3 py-2.5 text-xs ${strikeCount >= 5 ? 'bg-red-50 border border-red-200 text-red-800' : strikeCount >= 4 ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-slate-50 border border-slate-200 text-slate-700'}`}>
                    <strong>{strikeCount} notice{strikeCount !== 1 ? 's' : ''}</strong> in the last 12 months
                    {strikeCount >= 5 && ' — grounds for a termination order'}
                    {strikeCount === 4 && ' — VCAT threshold reached'}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Prepared by</label>
                  <input
                    value={vcatPreparedBy}
                    onChange={e => setVcatPreparedBy(e.target.value)}
                    placeholder={user?.name || 'Your name'}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                </div>
                {reports.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 mb-2">Previous reports</p>
                    <div className="space-y-1">
                      {reports.slice(0, 3).map(r => (
                        <div key={r.id} className="flex items-center justify-between text-xs text-slate-500 bg-slate-50 rounded px-2 py-1.5">
                          <span>Generated {formatDate(r.generated_at?.split('T')[0] || r.generated_at)}</span>
                          <span className="text-slate-400">by {r.generated_by}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowVcatModal(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]">Cancel</button>
                  <button type="button" onClick={handleGenerateVcat} disabled={vcatLoading}
                    className="flex-1 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-lg text-sm font-semibold min-h-[44px]">
                    {vcatLoading ? 'Generating…' : 'Download PDF'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Tab bar ── */}
      <div className="mb-5 overflow-x-auto">
        <div className="flex gap-1 bg-white rounded-xl border border-slate-200 shadow-sm p-1 min-w-max sm:min-w-0">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.key ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══ TAB: RENT SCHEDULE ══ */}
      {activeTab === 'schedule' && (
        <>
          {ledger.inArrears && (
            <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-amber-800">Rent overdue by {ledger.arrearsDays} day{ledger.arrearsDays !== 1 ? 's' : ''}</p>
                <p className="text-sm text-amber-700 mt-0.5">{formatCurrency(ledger.arrearsAmount)} outstanding · Paid to {formatDate(ledger.paidToDate)}</p>
              </div>
            </div>
          )}

          {/* VCAT info panel — shown when 14+ days overdue */}
          {ledger.inArrears && ledger.arrearsDays >= 14 && (
            <div className="mb-5 rounded-xl border border-violet-200 bg-violet-50 overflow-hidden">
              <button
                onClick={() => setShowVcatSidebar(v => !v)}
                className="w-full px-4 py-3 flex items-center justify-between text-left">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-violet-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                  <span className="text-sm font-semibold text-violet-800">VCAT Evidence — rent arrears {ledger.arrearsDays} days</span>
                  {strikeCount > 0 && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${strikeCount >= 4 ? 'bg-red-100 text-red-700' : 'bg-violet-100 text-violet-700'}`}>
                      {strikeCount} notice{strikeCount !== 1 ? 's' : ''} in 12 months
                    </span>
                  )}
                </div>
                <svg className={`w-4 h-4 text-violet-500 transition-transform ${showVcatSidebar ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showVcatSidebar && (
                <div className="px-4 pb-4 border-t border-violet-100">
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div className="bg-white rounded-lg p-3 border border-violet-100">
                      <p className="font-semibold text-violet-800 mb-1">Day 14+</p>
                      <p className="text-slate-600">You can issue a <strong>Notice to Vacate</strong> for rent arrears (Form 2).</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-violet-100">
                      <p className="font-semibold text-violet-800 mb-1">4 notices / 12 months</p>
                      <p className="text-slate-600">Four notices in 12 months meets the VCAT threshold to apply for possession.</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-violet-100">
                      <p className="font-semibold text-violet-800 mb-1">5+ notices</p>
                      <p className="text-slate-600">Five or more notices in 12 months are grounds for a <strong>termination order</strong>.</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => { setActiveTab('notices'); }}
                      className="text-xs bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                      Log a notice
                    </button>
                    <button
                      onClick={() => { setVcatPreparedBy(user?.name || ''); loadReports(); setShowVcatModal(true); }}
                      className="text-xs border border-violet-300 text-violet-700 hover:bg-violet-100 px-3 py-1.5 rounded-lg font-medium transition-colors">
                      Generate VCAT report
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-semibold text-slate-800">Rent schedule</h2>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-slate-400">{periods.length} period{periods.length !== 1 ? 's' : ''}</span>
                {statParts.map((p, i) => (
                  <React.Fragment key={i}>
                    <span className="text-slate-200">·</span>
                    <span className="font-semibold" style={{ color: p.color }}>{p.label}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-2.5 bg-slate-50 border-b border-slate-100 text-xs font-medium text-slate-500 uppercase tracking-wide">
              <div className="col-span-1">#</div>
              <div className="col-span-2">Due date</div>
              <div className="col-span-3">Period</div>
              <div className="col-span-2">Amount due</div>
              <div className="col-span-2">Received</div>
              <div className="col-span-1">Balance</div>
              <div className="col-span-1 text-right">Status</div>
            </div>
            <div className="divide-y divide-slate-100">
              {[...periods].reverse().map((period, idx) => {
                const isOverdue   = period.status === 'OVERDUE';
                const isExpanded  = expandedPeriod === period.periodNumber;
                const hasPayments = period.paymentsInPeriod.length > 0;
                const cfg = STATUS_CONFIG[period.status] || STATUS_CONFIG.OVERDUE;
                return (
                  <div key={period.periodNumber}
                    className={`transition-colors ${!isOverdue ? cfg.bg : ''}`}
                    style={isOverdue ? { backgroundColor: '#FFFAFA' } : undefined}>
                    <div
                      className={`grid grid-cols-12 gap-2 px-5 py-3.5 items-center ${hasPayments ? 'cursor-pointer hover:brightness-95' : ''}`}
                      onClick={() => hasPayments && setExpandedPeriod(isExpanded ? null : period.periodNumber)}>
                      <div className="col-span-1"><span className="text-xs text-slate-400 font-medium">{period.periodNumber}</span></div>
                      <div className="col-span-2">
                        <span className={`text-sm font-semibold ${isOverdue ? 'text-red-600' : 'text-slate-700'}`}>{formatDate(period.dueDate)}</span>
                      </div>
                      <div className="col-span-3"><span className="text-xs text-slate-500">{formatDate(period.dueDate)} – {formatDate(period.periodEnd)}</span></div>
                      <div className="col-span-2"><span className="text-sm text-slate-700">{formatCurrency(period.amountDue)}</span></div>
                      <div className="col-span-2">
                        {period.periodPaid > 0 ? (
                          <span className={`text-sm font-semibold ${period.periodPaid >= period.amountDue ? 'text-green-700' : 'text-amber-600'}`}>
                            {formatCurrency(period.periodPaid)}
                          </span>
                        ) : isOverdue
                          ? <span className="text-xs text-red-400 italic">nothing received</span>
                          : <span className="text-sm text-slate-400 italic">—</span>
                        }
                      </div>
                      <div className="col-span-1">
                        {idx === 0
                          ? <span className={`text-sm font-semibold font-mono ${period.runningBalance >= 0 ? 'text-green-700' : 'text-amber-700'}`}>
                              {period.runningBalance >= 0 ? formatCurrency(period.runningBalance) : `−${formatCurrency(Math.abs(period.runningBalance))}`}
                            </span>
                          : <span className="text-slate-200 text-sm">—</span>
                        }
                      </div>
                      <div className="col-span-1 flex items-center justify-end gap-1">
                        <PeriodStatusBadge status={period.status} />
                        {hasPayments && (
                          <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    {isExpanded && hasPayments && (
                      <div className="px-5 pb-3 pt-1 border-t border-slate-200/60 space-y-1.5">
                        {period.paymentsInPeriod.map(payment => (
                          editingPayment?.id === payment.id ? (
                            <form key={payment.id} onClick={e => e.stopPropagation()} onSubmit={handleSaveEdit}
                              className="bg-white border border-green-200 rounded-lg px-3 py-2.5">
                              {editError && <p className="text-xs text-red-600 mb-1">{editError}</p>}
                              <div className="flex flex-wrap gap-2 items-end">
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                  <input type="number" step="0.01" min="0.01" required value={editingPayment.amount}
                                    onChange={e => setEditingPayment(p => ({ ...p, amount: e.target.value }))}
                                    className="pl-5 pr-2 py-1.5 w-28 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500" />
                                </div>
                                <input type="date" required value={editingPayment.receivedDate}
                                  onChange={e => setEditingPayment(p => ({ ...p, receivedDate: e.target.value }))}
                                  className="px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500" />
                                <input value={editingPayment.notes}
                                  onChange={e => setEditingPayment(p => ({ ...p, notes: e.target.value }))}
                                  placeholder="notes" className="px-2 py-1.5 border border-slate-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500 w-32" />
                                <button type="submit" disabled={editLoading}
                                  className="px-3 py-1.5 bg-green-700 text-white rounded text-xs font-semibold min-h-[32px]">{editLoading ? '…' : 'Save'}</button>
                                <button type="button" onClick={e => { e.stopPropagation(); setEditingPayment(null); }}
                                  className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded text-xs min-h-[32px]">Cancel</button>
                              </div>
                            </form>
                          ) : (
                            <div key={payment.id} className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-2 text-xs">
                              <div className="flex items-center gap-3">
                                <span className="font-semibold text-slate-700 font-mono">{formatCurrency(payment.amount)}</span>
                                <span className="text-slate-500">received {formatDate(payment.received_date)}</span>
                                {payment.notes && <span className="text-slate-400 italic">{payment.notes}</span>}
                              </div>
                              <div className="flex items-center gap-0.5">
                                <button onClick={e => { e.stopPropagation(); startEditPayment(payment); }}
                                  className="text-slate-300 hover:text-green-600 p-1 rounded min-h-[28px] min-w-[28px] flex items-center justify-center">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                </button>
                                <button onClick={e => { e.stopPropagation(); handleDeletePayment(payment.id); }}
                                  className="text-slate-300 hover:text-red-400 p-1 rounded min-h-[28px] min-w-[28px] flex items-center justify-center">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ══ TAB: PAYMENTS ══ */}
      {activeTab === 'payments' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">All payments</h2>
            <span className="text-xs text-slate-400">{ledger.payments.length} recorded</span>
          </div>
          <div className="divide-y divide-slate-100">
            {ledger.payments.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <p className="text-sm text-slate-500">No payments recorded yet</p>
              </div>
            ) : (
              [...ledger.payments].reverse().map(payment => (
                editingPayment?.id === payment.id ? (
                  <form key={payment.id} onSubmit={handleSaveEdit} className="px-5 py-3 bg-green-50/40">
                    {editError && <p className="text-xs text-red-600 mb-2">{editError}</p>}
                    <div className="flex flex-wrap gap-2 items-end">
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                        <input type="number" step="0.01" min="0.01" required value={editingPayment.amount}
                          onChange={e => setEditingPayment(p => ({ ...p, amount: e.target.value }))}
                          className="pl-5 pr-2 py-1.5 w-28 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                      </div>
                      <input type="date" required value={editingPayment.receivedDate}
                        onChange={e => setEditingPayment(p => ({ ...p, receivedDate: e.target.value }))}
                        className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                      <input value={editingPayment.notes} onChange={e => setEditingPayment(p => ({ ...p, notes: e.target.value }))}
                        placeholder="notes" className="px-2 py-1.5 border border-slate-300 rounded text-sm flex-1 min-w-[120px] focus:outline-none focus:ring-1 focus:ring-green-500" />
                      <button type="submit" disabled={editLoading}
                        className="px-4 py-1.5 bg-green-700 text-white rounded text-sm font-semibold min-h-[36px]">{editLoading ? '…' : 'Save'}</button>
                      <button type="button" onClick={() => setEditingPayment(null)}
                        className="px-4 py-1.5 border border-slate-300 text-slate-600 rounded text-sm min-h-[36px]">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div key={payment.id} className="px-5 py-3 flex items-center justify-between gap-3 min-h-[56px]">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800 font-mono">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-slate-500">{formatDate(payment.received_date)}{payment.notes ? ` · ${payment.notes}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => startEditPayment(payment)}
                        className="text-slate-300 hover:text-green-600 p-1 rounded min-h-[36px] min-w-[36px] flex items-center justify-center">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                      </button>
                      <button onClick={() => handleDeletePayment(payment.id)}
                        className="text-slate-300 hover:text-red-400 p-1 rounded min-h-[36px] min-w-[36px] flex items-center justify-center">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                )
              ))
            )}
          </div>
        </div>
      )}

      {/* ══ TAB: INSPECTIONS ══ */}
      {activeTab === 'inspections' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">Inspection log</h2>
            <button onClick={() => setShowAddInsp(v => !v)}
              className="text-xs bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add
            </button>
          </div>

          {showAddInsp && (
            <form onSubmit={handleAddInsp} className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Log inspection</p>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Date</label>
                  <input type="date" required value={inspForm.inspectionDate}
                    onChange={e => setInspForm(f => ({ ...f, inspectionDate: e.target.value }))}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Type</label>
                  <select value={inspForm.type} onChange={e => setInspForm(f => ({ ...f, type: e.target.value }))}
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent">
                    {INSP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-xs text-slate-500 mb-1">Notes (optional)</label>
                  <input value={inspForm.notes} onChange={e => setInspForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. No issues found"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                </div>
                <button type="submit" className="px-4 py-2 bg-green-700 hover:bg-green-800 text-white rounded-lg text-sm font-semibold min-h-[38px]">Save</button>
                <button type="button" onClick={() => setShowAddInsp(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-600 hover:bg-slate-50 rounded-lg text-sm min-h-[38px]">Cancel</button>
              </div>
            </form>
          )}

          {inspLoading ? (
            <div className="px-5 py-8 text-center text-sm text-slate-400">Loading...</div>
          ) : inspections.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-semibold text-slate-600">No inspections logged yet</p>
              <p className="text-xs text-slate-400 mt-1">Record routine, entry and exit inspections here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {inspections.map(insp => (
                editingInsp?.id === insp.id ? (
                  <form key={insp.id} onSubmit={handleSaveInsp} className="px-5 py-3 bg-slate-50">
                    <div className="flex flex-wrap gap-2 items-end">
                      <input type="date" required value={editingInsp.inspection_date}
                        onChange={e => setEditingInsp(x => ({ ...x, inspection_date: e.target.value }))}
                        className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                      <select value={editingInsp.type} onChange={e => setEditingInsp(x => ({ ...x, type: e.target.value }))}
                        className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500">
                        {INSP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <input value={editingInsp.notes || ''} onChange={e => setEditingInsp(x => ({ ...x, notes: e.target.value }))}
                        placeholder="notes" className="flex-1 min-w-[120px] px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                      <button type="submit" className="px-3 py-1.5 bg-green-700 text-white rounded text-sm font-semibold min-h-[34px]">Save</button>
                      <button type="button" onClick={() => setEditingInsp(null)}
                        className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded text-sm min-h-[34px]">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div key={insp.id} className="px-5 py-3.5 flex items-center justify-between gap-3 min-h-[52px]">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {INSP_TYPES.find(t => t.value === insp.type)?.label || insp.type}
                          <span className="ml-2 text-xs font-normal text-slate-500">{formatDate(insp.inspection_date)}</span>
                        </p>
                        {insp.notes && <p className="text-xs text-slate-500 mt-0.5">{insp.notes}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => setEditingInsp({ ...insp })}
                        className="text-slate-300 hover:text-green-600 p-1 rounded min-h-[32px] min-w-[32px] flex items-center justify-center">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                      </button>
                      <button onClick={() => handleDeleteInsp(insp.id)}
                        className="text-slate-300 hover:text-red-400 p-1 rounded min-h-[32px] min-w-[32px] flex items-center justify-center">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ TAB: MAINTENANCE ══ */}
      {activeTab === 'maintenance' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-slate-800">Maintenance requests</h2>
              {maintenance.filter(m => m.status !== 'resolved').length > 0 && (
                <p className="text-xs text-amber-600 mt-0.5">{maintenance.filter(m => m.status !== 'resolved').length} open</p>
              )}
            </div>
            <button onClick={() => setShowAddMaint(v => !v)}
              className="text-xs bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Add
            </button>
          </div>

          {showAddMaint && (
            <form onSubmit={handleAddMaint} className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">New request</p>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-xs text-slate-500 mb-1">Title</label>
                    <input required value={maintForm.title} onChange={e => setMaintForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="e.g. Leaking tap in kitchen"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Priority</label>
                    <select value={maintForm.priority} onChange={e => setMaintForm(f => ({ ...f, priority: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent">
                      {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Description (optional)</label>
                  <input value={maintForm.description} onChange={e => setMaintForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="More details..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="px-4 py-2 bg-green-700 hover:bg-green-800 text-white rounded-lg text-sm font-semibold min-h-[38px]">Save</button>
                  <button type="button" onClick={() => setShowAddMaint(false)}
                    className="px-4 py-2 border border-slate-300 text-slate-600 hover:bg-white rounded-lg text-sm min-h-[38px]">Cancel</button>
                </div>
              </div>
            </form>
          )}

          {maintLoading ? (
            <div className="px-5 py-8 text-center text-sm text-slate-400">Loading...</div>
          ) : maintenance.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-semibold text-slate-600">No maintenance requests</p>
              <p className="text-xs text-slate-400 mt-1">Track repairs and issues for this property</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {maintenance.map(req => (
                editingMaint?.id === req.id ? (
                  <form key={req.id} onSubmit={handleSaveMaint} className="px-5 py-4 bg-slate-50 space-y-3">
                    <div className="flex flex-wrap gap-3">
                      <input required value={editingMaint.title} onChange={e => setEditingMaint(x => ({ ...x, title: e.target.value }))}
                        className="flex-1 min-w-[160px] px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                      <select value={editingMaint.priority} onChange={e => setEditingMaint(x => ({ ...x, priority: e.target.value }))}
                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500">
                        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                      <select value={editingMaint.status} onChange={e => setEditingMaint(x => ({ ...x, status: e.target.value }))}
                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500">
                        {MAINT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <input value={editingMaint.description || ''} onChange={e => setEditingMaint(x => ({ ...x, description: e.target.value }))}
                      placeholder="description"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                    <div className="flex gap-2">
                      <button type="submit" className="px-4 py-2 bg-green-700 text-white rounded-lg text-sm font-semibold min-h-[36px]">Save</button>
                      <button type="button" onClick={() => setEditingMaint(null)}
                        className="px-4 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm min-h-[36px]">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div key={req.id} className="px-5 py-4 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        req.status === 'resolved' ? 'bg-green-100' : req.priority === 'urgent' ? 'bg-red-100' : 'bg-amber-100'
                      }`}>
                        <svg className={`w-4 h-4 ${req.status === 'resolved' ? 'text-green-700' : req.priority === 'urgent' ? 'text-red-600' : 'text-amber-700'}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-slate-800">{req.title}</p>
                          <MaintStatusBadge status={req.status} />
                          <PriorityDot priority={req.priority} />
                        </div>
                        {req.description && <p className="text-xs text-slate-500 mt-0.5">{req.description}</p>}
                        <p className="text-xs text-slate-400 mt-0.5">
                          Added {formatDate(req.created_at?.split('T')[0] || req.created_at)}
                          {req.resolved_at && ` · Resolved ${formatDate(req.resolved_at)}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button onClick={() => setEditingMaint({ ...req })}
                        className="text-slate-300 hover:text-green-600 p-1 rounded min-h-[32px] min-w-[32px] flex items-center justify-center">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                      </button>
                      <button onClick={() => handleDeleteMaint(req.id)}
                        className="text-slate-300 hover:text-red-400 p-1 rounded min-h-[32px] min-w-[32px] flex items-center justify-center">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ TAB: EXPENSES ══ */}
      {activeTab === 'expenses' && (
        <div className="space-y-4">
          {/* FY summary + CSV export */}
          {expenses.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{fyLabel} total expenses</p>
                <p className="text-2xl font-bold text-slate-800 mt-1 font-mono">{formatCurrency(fyTotal)}</p>
                <p className="text-xs text-slate-400 mt-0.5">1 Jul – 30 Jun · {expenses.filter(e => e.expense_date >= fyStart && e.expense_date <= fyEnd).length} entries</p>
              </div>
              <a
                href={`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/properties/${id}/expenses/export?fy=${now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear()}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-xs font-medium text-slate-600 hover:text-slate-800 border border-slate-200 hover:border-slate-300 px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap flex-shrink-0">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download CSV
              </a>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Expense log</h2>
              <button onClick={() => setShowAddExp(v => !v)}
                className="text-xs bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add
              </button>
            </div>

            {showAddExp && (
              <form onSubmit={handleAddExp} className="px-5 py-4 border-b border-slate-100 bg-slate-50">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Record expense</p>
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Date</label>
                    <input type="date" required value={expForm.expenseDate}
                      onChange={e => setExpForm(f => ({ ...f, expenseDate: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Category</label>
                    <select value={expForm.category} onChange={e => setExpForm(f => ({ ...f, category: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent">
                      {EXPENSE_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <label className="block text-xs text-slate-500 mb-1">Description</label>
                    <input required value={expForm.description} onChange={e => setExpForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="e.g. Plumber - fix kitchen tap"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Amount (AUD)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                      <input type="number" step="0.01" min="0.01" required value={expForm.amount}
                        onChange={e => setExpForm(f => ({ ...f, amount: e.target.value }))}
                        className="pl-7 pr-3 py-2 w-28 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                    </div>
                  </div>
                  <button type="submit" className="px-4 py-2 bg-green-700 hover:bg-green-800 text-white rounded-lg text-sm font-semibold min-h-[38px]">Save</button>
                  <button type="button" onClick={() => setShowAddExp(false)}
                    className="px-4 py-2 border border-slate-300 text-slate-600 hover:bg-white rounded-lg text-sm min-h-[38px]">Cancel</button>
                </div>
              </form>
            )}

            {expLoading ? (
              <div className="px-5 py-8 text-center text-sm text-slate-400">Loading...</div>
            ) : expenses.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <p className="text-sm font-semibold text-slate-600">No expenses recorded</p>
                <p className="text-xs text-slate-400 mt-1">Track repairs, rates, insurance and other costs</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {expenses.map(exp => (
                  editingExp?.id === exp.id ? (
                    <form key={exp.id} onSubmit={handleSaveExp} className="px-5 py-3 bg-slate-50">
                      <div className="flex flex-wrap gap-2 items-end">
                        <input type="date" required value={editingExp.expense_date}
                          onChange={e => setEditingExp(x => ({ ...x, expense_date: e.target.value }))}
                          className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                        <select value={editingExp.category} onChange={e => setEditingExp(x => ({ ...x, category: e.target.value }))}
                          className="px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500">
                          {EXPENSE_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input required value={editingExp.description} onChange={e => setEditingExp(x => ({ ...x, description: e.target.value }))}
                          className="flex-1 min-w-[120px] px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                          <input type="number" step="0.01" min="0.01" required value={editingExp.amount}
                            onChange={e => setEditingExp(x => ({ ...x, amount: e.target.value }))}
                            className="pl-5 pr-2 py-1.5 w-24 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-green-500" />
                        </div>
                        <button type="submit" className="px-3 py-1.5 bg-green-700 text-white rounded text-sm font-semibold min-h-[34px]">Save</button>
                        <button type="button" onClick={() => setEditingExp(null)}
                          className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded text-sm min-h-[34px]">Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <div key={exp.id} className="px-5 py-3.5 flex items-center justify-between gap-3 min-h-[52px]">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{exp.category}</span>
                            <span className="text-sm font-semibold text-slate-800 font-mono">{formatCurrency(exp.amount)}</span>
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5">{exp.description}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{formatDate(exp.expense_date)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button onClick={() => setEditingExp({ ...exp })}
                          className="text-slate-300 hover:text-green-600 p-1 rounded min-h-[32px] min-w-[32px] flex items-center justify-center">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        </button>
                        <button onClick={() => handleDeleteExp(exp.id)}
                          className="text-slate-300 hover:text-red-400 p-1 rounded min-h-[32px] min-w-[32px] flex items-center justify-center">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ TAB: NOTICES ══ */}
      {activeTab === 'notices' && (
        <div className="space-y-4">
          {/* Strike counter card */}
          <div className={`rounded-2xl border p-4 flex items-center justify-between gap-4 ${
            strikeCount >= 5 ? 'bg-red-50 border-red-200' :
            strikeCount >= 4 ? 'bg-amber-50 border-amber-200' :
            strikeCount >= 1 ? 'bg-violet-50 border-violet-200' :
            'bg-white border-slate-200'
          }`}>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-1 text-slate-500">Notices in last 12 months</p>
              <p className={`text-3xl font-bold ${strikeCount >= 5 ? 'text-red-700' : strikeCount >= 4 ? 'text-amber-700' : strikeCount >= 1 ? 'text-violet-700' : 'text-slate-400'}`}>
                {strikeCount} <span className="text-base font-normal">{strikeCount === 1 ? 'notice' : 'notices'}</span>
              </p>
              <p className="text-xs mt-1 text-slate-500">
                {strikeCount === 0 && 'No notices issued in the last 12 months'}
                {strikeCount === 1 && '3 more to reach VCAT threshold'}
                {strikeCount === 2 && '2 more to reach VCAT threshold'}
                {strikeCount === 3 && '1 more to reach VCAT threshold'}
                {strikeCount === 4 && 'VCAT threshold reached — eligible to apply for possession'}
                {strikeCount >= 5 && 'Grounds for termination order'}
              </p>
            </div>
            {ledger.inArrears && ledger.arrearsDays >= 14 && (
              <button
                onClick={() => { setVcatPreparedBy(user?.name || ''); loadReports(); setShowVcatModal(true); }}
                className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap flex-shrink-0">
                VCAT Report
              </button>
            )}
          </div>

          {/* Notices list */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Notice log</h2>
              <button onClick={() => setShowAddNotice(v => !v)}
                className="text-xs bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add notice
              </button>
            </div>

            {showAddNotice && (
              <form onSubmit={handleAddNotice} className="px-5 py-4 border-b border-slate-100 bg-slate-50 space-y-3">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Log a formal notice</p>
                <div className="flex flex-wrap gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Notice type</label>
                    <select value={noticeForm.noticeType} onChange={e => setNoticeForm(f => ({ ...f, noticeType: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                      <option value="notice_to_vacate">Notice to Vacate</option>
                      <option value="notice_to_remedy">Notice to Remedy</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Date issued</label>
                    <input type="date" required value={noticeForm.issuedDate}
                      onChange={e => setNoticeForm(f => ({ ...f, issuedDate: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Delivery method</label>
                    <select value={noticeForm.deliveryMethod} onChange={e => setNoticeForm(f => ({ ...f, deliveryMethod: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent">
                      <option value="email">Email</option>
                      <option value="post">Post</option>
                      <option value="hand_delivered">Hand delivered</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Vacate date (if applicable)</label>
                    <input type="date" value={noticeForm.vacateDate}
                      onChange={e => setNoticeForm(f => ({ ...f, vacateDate: e.target.value }))}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                  </div>
                </div>
                {/* Consent warning — shown when Email is selected */}
                {noticeForm.deliveryMethod === 'email' && consentStatus && !consentStatus.consentActive && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-xs text-red-700">
                      <strong>No electronic consent on file.</strong> You cannot legally serve this notice by email without the tenant's written consent under Victorian tenancy law. Use Post or Hand delivered, or{' '}
                      <button type="button" onClick={() => { setShowAddNotice(false); setShowManualConsentModal(true); }} className="underline font-medium">record consent first</button>.
                    </p>
                  </div>
                )}
                {noticeForm.deliveryMethod === 'email' && consentStatus?.consentActive && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    <p className="text-xs text-green-700">Electronic consent on file — this notice can be legally served by email.</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Notes (optional)</label>
                  <input value={noticeForm.notes} onChange={e => setNoticeForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. Emailed at 9:15am, tenant acknowledged receipt"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent" />
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-semibold min-h-[38px]">Save</button>
                  <button type="button" onClick={() => setShowAddNotice(false)}
                    className="px-4 py-2 border border-slate-300 text-slate-600 hover:bg-white rounded-lg text-sm min-h-[38px]">Cancel</button>
                </div>
              </form>
            )}

            {noticesLoading ? (
              <div className="px-5 py-8 text-center text-sm text-slate-400">Loading…</div>
            ) : notices.length === 0 ? (
              <div className="px-5 py-10 text-center">
                <p className="text-sm font-semibold text-slate-600">No notices logged</p>
                <p className="text-xs text-slate-400 mt-1">Record formal notices issued to the tenant here for VCAT evidence</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notices.map(notice => {
                  const isRecent = new Date(notice.issued_date) >= twelveMonthsAgo;
                  const typeLabel = notice.notice_type === 'notice_to_vacate' ? 'Notice to Vacate'
                    : notice.notice_type === 'notice_to_remedy' ? 'Notice to Remedy' : 'Notice';
                  const deliveryLabel = notice.delivery_method === 'hand_delivered' ? 'Hand delivered'
                    : notice.delivery_method === 'post' ? 'Post' : 'Email';
                  return editingNotice?.id === notice.id ? (
                    <form key={notice.id} onSubmit={handleSaveNotice} className="px-5 py-4 bg-slate-50 space-y-3">
                      <div className="flex flex-wrap gap-3">
                        <select value={editingNotice.notice_type} onChange={e => setEditingNotice(x => ({ ...x, notice_type: e.target.value }))}
                          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500">
                          <option value="notice_to_vacate">Notice to Vacate</option>
                          <option value="notice_to_remedy">Notice to Remedy</option>
                          <option value="other">Other</option>
                        </select>
                        <input type="date" required value={editingNotice.issued_date}
                          onChange={e => setEditingNotice(x => ({ ...x, issued_date: e.target.value }))}
                          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500" />
                        <select value={editingNotice.delivery_method} onChange={e => setEditingNotice(x => ({ ...x, delivery_method: e.target.value }))}
                          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500">
                          <option value="email">Email</option>
                          <option value="post">Post</option>
                          <option value="hand_delivered">Hand delivered</option>
                        </select>
                        <input type="date" value={editingNotice.vacate_date || ''}
                          onChange={e => setEditingNotice(x => ({ ...x, vacate_date: e.target.value }))}
                          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500" />
                      </div>
                      <input value={editingNotice.notes || ''} onChange={e => setEditingNotice(x => ({ ...x, notes: e.target.value }))}
                        placeholder="notes"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-violet-500" />
                      <div className="flex gap-2">
                        <button type="submit" className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-semibold min-h-[36px]">Save</button>
                        <button type="button" onClick={() => setEditingNotice(null)}
                          className="px-4 py-2 border border-slate-300 text-slate-600 rounded-lg text-sm min-h-[36px]">Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <div key={notice.id} className="px-5 py-3.5 flex items-start justify-between gap-3 min-h-[52px]">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isRecent ? 'bg-violet-100' : 'bg-slate-100'}`}>
                          <svg className={`w-4 h-4 ${isRecent ? 'text-violet-600' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-slate-800">{typeLabel}</p>
                            {isRecent && <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-medium">counts toward 12-month total</span>}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Issued {formatDate(notice.issued_date)} · {deliveryLabel}
                            {notice.vacate_date && ` · Vacate by ${formatDate(notice.vacate_date)}`}
                          </p>
                          {notice.notes && <p className="text-xs text-slate-400 mt-0.5 italic">{notice.notes}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button onClick={() => setEditingNotice({ ...notice })}
                          className="text-slate-300 hover:text-violet-500 p-1 rounded min-h-[32px] min-w-[32px] flex items-center justify-center">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        </button>
                        <button onClick={() => handleDeleteNotice(notice.id)}
                          className="text-slate-300 hover:text-red-400 p-1 rounded min-h-[32px] min-w-[32px] flex items-center justify-center">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Previous VCAT reports */}
          {reports.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100">
                <h2 className="font-semibold text-slate-800">Report history</h2>
                <p className="text-xs text-slate-400 mt-0.5">VCAT reports previously generated for this tenancy</p>
              </div>
              <div className="divide-y divide-slate-100">
                {reports.map(r => (
                  <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3 min-h-[48px]">
                    <div>
                      <p className="text-sm font-medium text-slate-700">VCAT Evidence Report</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {formatDate(r.generated_at?.split('T')[0] || r.generated_at)} · by {r.generated_by}
                        {r.amount_outstanding_cents > 0 && ` · ${formatCurrency(r.amount_outstanding_cents / 100)} outstanding at time`}
                      </p>
                    </div>
                    <span className="text-xs text-slate-400">{r.periods_covered} periods</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ TAB: TENANT ══ */}
      {activeTab === 'tenant' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h2 className="font-semibold text-slate-800 mb-4">Tenant details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Name</p>
                <p className="text-sm font-semibold text-slate-800">{tenancy.tenant_name}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Email</p>
                <p className="text-sm text-slate-700">{tenancy.tenant_email || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Lease start</p>
                <p className="text-sm text-slate-700">{formatDate(tenancy.lease_start_date)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Fortnightly rent</p>
                <p className="text-sm font-semibold text-slate-800 font-mono">{formatCurrency(tenancy.fortnightly_rent)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h2 className="font-semibold text-slate-800 mb-1">SMS reminders</h2>
            <p className="text-xs text-slate-400 mb-4">Tenant phone for automated arrears alerts</p>
            {editingPhone ? (
              <form onSubmit={handleSavePhone} className="flex items-center gap-2 flex-wrap">
                <input type="tel" value={phoneValue} onChange={e => setPhoneValue(e.target.value)}
                  placeholder="+61 4xx xxx xxx" autoFocus
                  className="text-sm px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-48" />
                <button type="submit" disabled={phoneLoading}
                  className="text-sm bg-green-700 hover:bg-green-800 disabled:opacity-60 text-white px-4 py-2 rounded-lg font-medium min-h-[36px]">
                  {phoneLoading ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={() => { setEditingPhone(false); setPhoneError(''); }}
                  className="text-sm border border-slate-300 text-slate-600 hover:bg-slate-50 px-4 py-2 rounded-lg min-h-[36px]">Cancel</button>
                {phoneError && <span className="text-xs text-red-600 w-full">{phoneError}</span>}
              </form>
            ) : tenancy.tenant_phone ? (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-sm font-medium text-green-800">{tenancy.tenant_phone}</span>
                  <span className="text-xs text-green-600">· SMS on</span>
                </div>
                <button onClick={() => { setPhoneValue(tenancy.tenant_phone || ''); setEditingPhone(true); }}
                  className="text-xs text-slate-500 hover:text-slate-700 border border-slate-200 hover:border-slate-300 px-3 py-2 rounded-lg transition-colors">Edit</button>
              </div>
            ) : (
              <button onClick={() => { setPhoneValue(''); setEditingPhone(true); }}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 hover:border-slate-400 px-4 py-2.5 rounded-lg transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add phone number
              </button>
            )}
          </div>

          {tenancy.invite_token ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h2 className="font-semibold text-slate-800 mb-1">Tenant portal invite</h2>
              <p className="text-xs text-slate-400 mb-4">{tenancy.tenant_name} hasn't set up their account yet</p>
              <div className="flex items-center gap-2 mb-3">
                <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-slate-600 truncate">{inviteLink}</code>
                <button onClick={() => { navigator.clipboard.writeText(inviteLink); showToast('Link copied', 'info'); }}
                  className="text-xs border border-slate-300 text-slate-600 hover:bg-slate-50 px-3 py-2.5 rounded-lg font-medium whitespace-nowrap min-h-[38px] transition-colors">Copy link</button>
              </div>
              <button
                onClick={handleResendInvite}
                disabled={resendLoading}
                className="flex items-center gap-2 text-sm bg-green-700 hover:bg-green-800 disabled:opacity-60 text-white px-4 py-2.5 rounded-lg font-medium transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {resendLoading ? 'Sending…' : 'Send invite email'}
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">Portal active</p>
                  <p className="text-xs text-slate-500 mt-0.5">{tenancy.tenant_name} has set up their account</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Electronic consent card ── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-semibold text-slate-800">Electronic consent</h2>
              {consentStatusLoading && <span className="text-xs text-slate-400">Loading…</span>}
            </div>
            <p className="text-xs text-slate-400 mb-4">Required under Victorian tenancy law to serve formal notices by email</p>

            {!consentStatus ? (
              !consentStatusLoading && (
                <p className="text-sm text-slate-400 italic">Loading consent status…</p>
              )
            ) : consentStatus.consentActive ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Consented
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div>
                    <p className="text-slate-400 uppercase tracking-wide font-medium mb-0.5">Date</p>
                    <p className="text-slate-700">{consentStatus.consentDate ? formatDate(consentStatus.consentDate.split('T')[0]) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 uppercase tracking-wide font-medium mb-0.5">Method</p>
                    <p className="text-slate-700 capitalize">{consentStatus.consentMethod === 'invite_flow' ? 'Tenant portal' : consentStatus.consentMethod === 'landlord_recorded' ? 'Landlord recorded' : consentStatus.consentMethod || '—'}</p>
                  </div>
                  {consentStatus.consentIpMasked && (
                    <div>
                      <p className="text-slate-400 uppercase tracking-wide font-medium mb-0.5">IP (masked)</p>
                      <p className="text-slate-500 font-mono">{consentStatus.consentIpMasked}</p>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleGenerateConsentPdf}
                  className="inline-flex items-center gap-1.5 text-xs text-brand-600 hover:text-brand-700 border border-brand-200 hover:bg-brand-50 px-3 py-1.5 rounded-lg transition-colors mt-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download consent certificate
                </button>
              </div>
            ) : consentStatus.consentWithdrawn ? (
              <div className="space-y-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
                  Withdrawn
                </span>
                {consentStatus.consentWithdrawnDate && (
                  <p className="text-xs text-slate-400">Withdrawn on {formatDate(consentStatus.consentWithdrawnDate.split('T')[0])}</p>
                )}
                <p className="text-sm text-slate-600">The tenant has withdrawn consent. Formal notices must be delivered by hand or post.</p>
                <button
                  onClick={() => { setManualConsentForm({ consentDate: todayISO, consentHow: 'Included in signed lease agreement', notes: '' }); setShowManualConsentModal(true); }}
                  className="text-sm border border-slate-300 text-slate-600 hover:bg-slate-50 px-4 py-2 rounded-lg transition-colors">
                  Record new consent
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600">
                  Not consented
                </span>
                <p className="text-sm text-slate-600">
                  {consentStatus.consentDate ? 'The tenant declined electronic notices.' : 'The tenant has not yet responded to the consent request.'}
                  {' '}If consent was obtained another way (e.g. signed lease), you can record it here.
                </p>
                <button
                  onClick={() => { setManualConsentForm({ consentDate: todayISO, consentHow: 'Included in signed lease agreement', notes: '' }); setShowManualConsentModal(true); }}
                  className="text-sm border border-slate-300 text-slate-600 hover:bg-slate-50 px-4 py-2 rounded-lg transition-colors">
                  Record consent manually
                </button>
              </div>
            )}
          </div>
        </div>
      )}


      {activeTab === 'documents' && (
        <div className="space-y-6">
          {docsLoading && <p className="text-sm text-slate-400">Loading documents…</p>}

          {/* SECTION 1 — Pre-Inspection */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-800 mb-1">Pre-Inspection Report &amp; Photos</h3>
            <p className="text-xs text-slate-500 mb-4">Upload the condition report and photos taken before the tenancy began. These are important evidence if there is a dispute about the property condition at the end of the lease.</p>

            {/* Image gallery */}
            {documents.filter(d => d.document_type === 'inspection_image').length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Photos</p>
                <div className="grid grid-cols-3 gap-2">
                  {documents.filter(d => d.document_type === 'inspection_image').map(doc => (
                    <div key={doc.id} className="relative group rounded-xl overflow-hidden bg-slate-100 aspect-square cursor-pointer" onClick={() => setLightboxDoc(doc)}>
                      <img src={getDocFileUrl(doc.id)} alt={doc.file_name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display='none'; }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <svg className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteDoc(doc.id); }}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* PDF condition reports */}
            {documents.filter(d => d.document_type === 'pre_inspection_report').length > 0 && (
              <div className="mb-4 space-y-2">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Condition Reports</p>
                {documents.filter(d => d.document_type === 'pre_inspection_report').map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <svg className="w-8 h-8 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{doc.file_name}</p>
                      <p className="text-xs text-slate-400">{fmtFileSize(doc.file_size)} · {new Date(doc.uploaded_at).toLocaleDateString('en-AU')}</p>
                    </div>
                    <a href={getDocFileUrl(doc.id)} target="_blank" rel="noreferrer"
                      className="text-xs text-brand-600 hover:text-brand-700 px-2 py-1 rounded border border-brand-200 hover:bg-brand-50 transition-colors">
                      View
                    </a>
                    <button onClick={() => handleDeleteDoc(doc.id)}
                      className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-200 hover:bg-red-50 transition-colors">
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}

            <UploadZone
              sectionKey="pre_inspection"
              acceptedTypes="image/jpeg,image/png,image/heic,image/webp,application/pdf"
              sizeNote="PDF up to 20MB, images up to 10MB"
              onFile={(file) => {
                const type = file.type === 'application/pdf' ? 'pre_inspection_report' : 'inspection_image';
                handleDocUpload(file, type, 'pre_inspection');
              }}
              uploading={docUploading['pre_inspection']}
              uploadError={docUploadError['pre_inspection']}
            />
          </div>

          {/* SECTION 2 — Bond Documents */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-800 mb-1">Bond Receipt &amp; Lodgement</h3>
            <p className="text-xs text-slate-500 mb-4">Upload your bond receipt from the tenant and the RTBA lodgement confirmation. These prove the bond was collected and lodged correctly.</p>

            {documents.filter(d => d.document_type === 'bond_receipt' || d.document_type === 'bond_lodgement').length > 0 && (
              <div className="mb-4 space-y-2">
                {documents.filter(d => d.document_type === 'bond_receipt' || d.document_type === 'bond_lodgement').map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <svg className="w-7 h-7 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-slate-800 truncate">{doc.file_name}</p>
                        <DocTypeLabel type={doc.document_type} />
                      </div>
                      <p className="text-xs text-slate-400">{fmtFileSize(doc.file_size)} · {new Date(doc.uploaded_at).toLocaleDateString('en-AU')}</p>
                    </div>
                    <a href={getDocFileUrl(doc.id)} target="_blank" rel="noreferrer"
                      className="text-xs text-brand-600 hover:text-brand-700 px-2 py-1 rounded border border-brand-200 hover:bg-brand-50 transition-colors">
                      View
                    </a>
                    <button onClick={() => handleDeleteDoc(doc.id)}
                      className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-200 hover:bg-red-50 transition-colors">
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}

            <UploadZone
              sectionKey="bond"
              acceptedTypes="application/pdf,image/jpeg,image/png"
              sizeNote="PDF or image up to 20MB"
              onFile={(file) => {
                const type = window.confirm('Is this the RTBA lodgement confirmation?\n\nOK = Bond Lodgement · Cancel = Bond Receipt')
                  ? 'bond_lodgement' : 'bond_receipt';
                handleDocUpload(file, type, 'bond');
              }}
              uploading={docUploading['bond']}
              uploadError={docUploadError['bond']}
            />
          </div>

          {/* SECTION 3 — Other Documents */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-semibold text-slate-800 mb-1">Other Documents</h3>
            <p className="text-xs text-slate-500 mb-4">Lease agreement, correspondence, or any other tenancy documents.</p>

            {documents.filter(d => d.document_type === 'lease_agreement' || d.document_type === 'other').length > 0 && (
              <div className="mb-4 space-y-2">
                {documents.filter(d => d.document_type === 'lease_agreement' || d.document_type === 'other').map(doc => (
                  <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <svg className="w-7 h-7 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-medium text-slate-800 truncate">{doc.file_name}</p>
                        <DocTypeLabel type={doc.document_type} />
                      </div>
                      {doc.description && <p className="text-xs text-slate-500 italic">{doc.description}</p>}
                      <p className="text-xs text-slate-400">{fmtFileSize(doc.file_size)} · {new Date(doc.uploaded_at).toLocaleDateString('en-AU')}</p>
                    </div>
                    <a href={getDocFileUrl(doc.id)} target="_blank" rel="noreferrer"
                      className="text-xs text-brand-600 hover:text-brand-700 px-2 py-1 rounded border border-brand-200 hover:bg-brand-50 transition-colors">
                      View
                    </a>
                    <button onClick={() => handleDeleteDoc(doc.id)}
                      className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded border border-red-200 hover:bg-red-50 transition-colors">
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}

            <UploadZone
              sectionKey="other"
              acceptedTypes="application/pdf,image/jpeg,image/png"
              sizeNote="PDF or image up to 20MB"
              onFile={(file) => {
                const type = window.confirm('Is this a lease agreement?\n\nOK = Lease Agreement · Cancel = Other document')
                  ? 'lease_agreement' : 'other';
                handleDocUpload(file, type, 'other');
              }}
              uploading={docUploading['other']}
              uploadError={docUploadError['other']}
            />
          </div>
        </div>
      )}


      {/* ── Import Ledger Modal ── */}
      {showImportModal && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={resetImportModal} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col pointer-events-auto">

              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">Import payment history</h2>
                  <div className="flex gap-2 mt-1">
                    {[1,2,3].map(s => (
                      <div key={s} className={`w-6 h-1.5 rounded-full ${importStep >= s ? 'bg-brand-600' : 'bg-slate-200'}`} />
                    ))}
                  </div>
                </div>
                <button onClick={resetImportModal} className="text-slate-400 hover:text-slate-600 p-1 rounded">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">

                {/* ── STEP 1: Upload ── */}
                {importStep === 1 && (
                  <div className="p-6 space-y-5">
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Upload your existing rent ledger from your bank, property manager, or previous software.
                      Our AI will extract the payment records automatically.
                    </p>
                    <p className="text-xs text-slate-400">
                      Supported: Bank statements, agent ledgers, Excel exports (as PDF), handwritten or printed ledgers, photos of documents
                    </p>

                    {/* Upload zone */}
                    <div
                      onClick={() => document.getElementById('import-ledger-input').click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setImportFile(f); }}
                      className="border-2 border-dashed border-slate-300 bg-slate-50 hover:border-brand-400 hover:bg-blue-50 rounded-xl p-8 text-center cursor-pointer transition-colors">
                      <svg className="w-10 h-10 mx-auto mb-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      {importFile ? (
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{importFile.name}</p>
                          <p className="text-xs text-slate-400 mt-1">{fmtFileSize(importFile.size)} · Ready to extract — click Analyse</p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm font-medium text-slate-700">Drop your ledger here or click to browse</p>
                          <p className="text-xs text-slate-400 mt-1">PDF or image — max 20MB</p>
                        </div>
                      )}
                      <input id="import-ledger-input" type="file" accept="application/pdf,image/jpeg,image/png,image/heic,image/webp" className="hidden"
                        onChange={(e) => { if (e.target.files[0]) setImportFile(e.target.files[0]); }} />
                    </div>

                    {/* Lease context box */}
                    {data && (
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-500">
                        <span className="font-medium text-slate-600">AI will look for payments matching: </span>
                        Tenant: <strong>{data.tenancy?.tenant_name}</strong> · Rent: <strong>${data.tenancy?.fortnightly_rent}/fn</strong> · Start: <strong>{formatDate(data.tenancy?.lease_start_date)}</strong>
                      </div>
                    )}

                    {importError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{importError}</div>
                    )}
                  </div>
                )}

                {/* ── STEP 2: Review ── */}
                {importStep === 2 && importExtraction && (
                  <div className="p-6 space-y-4">
                    {/* Confidence banner */}
                    <div className={`flex items-center gap-3 p-3 rounded-xl border ${
                      importExtraction.confidence === 'high'   ? 'bg-green-50 border-green-200' :
                      importExtraction.confidence === 'medium' ? 'bg-amber-50 border-amber-200' :
                                                                 'bg-red-50 border-red-200'
                    }`}>
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        importExtraction.confidence === 'high' ? 'bg-green-500' :
                        importExtraction.confidence === 'medium' ? 'bg-amber-500' : 'bg-red-500'
                      }`} />
                      <div>
                        <p className={`text-sm font-semibold ${
                          importExtraction.confidence === 'high' ? 'text-green-800' :
                          importExtraction.confidence === 'medium' ? 'text-amber-800' : 'text-red-800'
                        }`}>
                          {importExtraction.confidence === 'high' ? 'High confidence extraction' :
                           importExtraction.confidence === 'medium' ? 'Review carefully' : 'Low confidence — verify all entries'}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Source: {importExtraction.source_type?.replace('_',' ') || 'unknown'}
                        </p>
                      </div>
                    </div>

                    {/* Warnings */}
                    {importExtraction.warnings?.length > 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-xs font-semibold text-amber-800 mb-1">Warnings</p>
                        {importExtraction.warnings.map((w,i) => <p key={i} className="text-xs text-amber-700">• {w}</p>)}
                      </div>
                    )}

                    {/* Notes */}
                    {importExtraction.notes && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700">
                        {importExtraction.notes}
                      </div>
                    )}

                    {/* Summary bar */}
                    {(() => {
                      const sel = importRows.filter(r => r.selected && !r.isDuplicate);
                      const total = sel.reduce((s,r) => s + (parseFloat(r.amount)||0), 0);
                      const dups = importRows.filter(r => r.isDuplicate).length;
                      return (
                        <div className="flex flex-wrap gap-3 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
                          <span><strong className="text-slate-800">{importRows.length}</strong> payments found</span>
                          <span>·</span>
                          <span><strong className="text-slate-800">${total.toFixed(2)}</strong> selected total</span>
                          {dups > 0 && <><span>·</span><span className="text-amber-600"><strong>{dups}</strong> duplicates will be skipped</span></>}
                        </div>
                      );
                    })()}

                    {/* Select all */}
                    <div className="flex items-center gap-2 text-xs">
                      <input type="checkbox"
                        checked={importRows.filter(r => !r.isDuplicate).every(r => r.selected)}
                        onChange={(e) => setImportRows(prev => prev.map(r => r.isDuplicate ? r : { ...r, selected: e.target.checked }))}
                        className="w-4 h-4 rounded border-slate-300 text-brand-600"
                      />
                      <span className="text-slate-500">Select all / Deselect all</span>
                    </div>

                    {/* Payments table */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="w-8 px-3 py-2"></th>
                            <th className="px-3 py-2 text-left font-medium text-slate-500">Date</th>
                            <th className="px-3 py-2 text-right font-medium text-slate-500">Amount</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-500 hidden sm:table-cell">Description</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-500">Conf.</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-500">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importRows.map((row, idx) => (
                            <tr key={row._key} className={`border-b border-slate-100 last:border-0 ${row.isDuplicate ? 'opacity-50' : ''}`}>
                              <td className="px-3 py-2 text-center">
                                <input type="checkbox" checked={row.selected} disabled={row.isDuplicate}
                                  onChange={(e) => setImportRows(prev => prev.map((r,i) => i===idx ? { ...r, selected: e.target.checked } : r))}
                                  className="w-4 h-4 rounded border-slate-300 text-brand-600"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input type="date" value={row.payment_date}
                                  onChange={(e) => setImportRows(prev => prev.map((r,i) => i===idx ? { ...r, payment_date: e.target.value } : r))}
                                  className="border border-slate-200 rounded px-2 py-1 text-xs w-32 focus:outline-none focus:ring-1 focus:ring-brand-400"
                                />
                              </td>
                              <td className="px-3 py-2 text-right">
                                <input type="number" step="0.01" value={row.amount}
                                  onChange={(e) => setImportRows(prev => prev.map((r,i) => i===idx ? { ...r, amount: e.target.value } : r))}
                                  className="border border-slate-200 rounded px-2 py-1 text-xs w-24 text-right focus:outline-none focus:ring-1 focus:ring-brand-400"
                                />
                              </td>
                              <td className="px-3 py-2 text-slate-400 hidden sm:table-cell max-w-[140px] truncate">{row.description}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-block w-2 h-2 rounded-full ${
                                  row.confidence === 'high' ? 'bg-green-500' :
                                  row.confidence === 'medium' ? 'bg-amber-500' : 'bg-red-500'
                                }`} title={`${row.confidence} confidence`} />
                              </td>
                              <td className="px-3 py-2 text-center">
                                {row.isDuplicate ? (
                                  <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs">Duplicate</span>
                                ) : !row.payment_date || !row.amount ? (
                                  <span className="inline-block px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs">Invalid</span>
                                ) : (
                                  <span className="inline-block px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">New</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {importError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{importError}</div>
                    )}
                  </div>
                )}

                {/* ── STEP 3: Complete ── */}
                {importStep === 3 && importResult && (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-4">Import complete!</h3>
                    <div className="space-y-2 text-sm mb-6">
                      <p className="text-green-700 flex items-center justify-center gap-2">
                        <span className="text-green-500">✓</span>
                        <strong>{importResult.imported}</strong> payment{importResult.imported !== 1 ? 's' : ''} imported successfully
                      </p>
                      {importResult.skipped > 0 && (
                        <p className="text-slate-500 flex items-center justify-center gap-2">
                          <span>↷</span>
                          <strong>{importResult.skipped}</strong> duplicate{importResult.skipped !== 1 ? 's' : ''} skipped (already existed)
                        </p>
                      )}
                      {importResult.errors > 0 && (
                        <p className="text-red-600 flex items-center justify-center gap-2">
                          <span>✗</span>
                          <strong>{importResult.errors}</strong> {importResult.errors !== 1 ? 'entries' : 'entry'} skipped (invalid data)
                        </p>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mb-6">
                      Your ledger has been updated. The paid-to-date and all balances have been recalculated.
                    </p>
                    <button onClick={resetImportModal}
                      className="bg-brand-600 hover:bg-brand-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors">
                      View updated ledger
                    </button>
                  </div>
                )}

              </div>

              {/* Footer actions */}
              {importStep !== 3 && (
                <div className="px-6 py-4 border-t border-slate-100 flex justify-between flex-shrink-0">
                  {importStep === 2 ? (
                    <button onClick={() => { setImportStep(1); setImportError(''); }}
                      className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                      ← Back
                    </button>
                  ) : (
                    <button onClick={resetImportModal}
                      className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors">
                      Cancel
                    </button>
                  )}

                  {importStep === 1 && (
                    <button
                      onClick={handleImportAnalyse}
                      disabled={!importFile || importLoading}
                      className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2 rounded-xl text-sm font-semibold transition-colors">
                      {importLoading ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Reading your document…
                        </>
                      ) : 'Analyse with AI →'}
                    </button>
                  )}

                  {importStep === 2 && (
                    <button
                      onClick={handleImportConfirm}
                      disabled={importRows.filter(r => r.selected && !r.isDuplicate).length === 0 || importLoading}
                      className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2 rounded-xl text-sm font-semibold transition-colors">
                      {importLoading ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Importing…
                        </>
                      ) : `Import ${importRows.filter(r => r.selected && !r.isDuplicate).length} selected payments →`}
                    </button>
                  )}
                </div>
              )}

            </div>
          </div>
        </>
      )}

      {/* ── Image Lightbox ── */}
      {lightboxDoc && (
        <>
          <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightboxDoc(null)}>
            <button className="absolute top-4 right-4 text-white/70 hover:text-white p-2" onClick={() => setLightboxDoc(null)}>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={getDocFileUrl(lightboxDoc.id)}
              alt={lightboxDoc.file_name}
              className="max-w-full max-h-full object-contain rounded-xl"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="absolute bottom-4 left-0 right-0 text-center text-white/60 text-sm">{lightboxDoc.file_name}</p>
          </div>
        </>
      )}

      {/* ── Manual Consent Modal ── */}
      {showManualConsentModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowManualConsentModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-slate-800">Record manual consent</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Document that {tenancy.tenant_name} has consented to electronic notices</p>
                </div>
                <button onClick={() => setShowManualConsentModal(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <form onSubmit={handleManualConsent} className="p-5 space-y-4">
                <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800">
                  Use this only if the tenant consented outside the portal — e.g. the lease agreement included a consent clause, or the tenant signed a separate form.
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Date consent was given</label>
                  <input type="date" required value={manualConsentForm.consentDate}
                    onChange={e => setManualConsentForm(f => ({ ...f, consentDate: e.target.value }))}
                    max={todayISO}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">How was consent obtained?</label>
                  <select value={manualConsentForm.consentHow}
                    onChange={e => setManualConsentForm(f => ({ ...f, consentHow: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent">
                    <option value="Included in signed lease agreement">Included in signed lease agreement</option>
                    <option value="Signed separate consent form">Signed separate consent form</option>
                    <option value="Email confirmation from tenant">Email confirmation from tenant</option>
                    <option value="Verbal agreement — confirmed in writing">Verbal agreement — confirmed in writing</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Notes (optional)</label>
                  <input value={manualConsentForm.notes}
                    onChange={e => setManualConsentForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. Clause 12.3 of lease signed 1 Jan 2025"
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" required checked={manualConsentConfirmed}
                    onChange={e => setManualConsentConfirmed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500 flex-shrink-0 cursor-pointer" />
                  <span className="text-sm text-slate-600 leading-relaxed">
                    I confirm that {tenancy.tenant_name} has given written consent to receive formal notices by email, and I have a record of this consent.
                  </span>
                </label>
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowManualConsentModal(false)}
                    className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 min-h-[44px]">Cancel</button>
                  <button type="submit" disabled={manualConsentLoading || !manualConsentConfirmed}
                    className="flex-1 px-4 py-2.5 bg-green-700 hover:bg-green-800 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold min-h-[44px]">
                    {manualConsentLoading ? 'Saving…' : 'Record consent'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
